import React, { Fragment } from "react";
import { Flex, Box } from "grid-styled";
import { withStateHandlers } from "recompose";
import { graphql, compose } from "react-apollo";
import { gql } from "apollo-boost";
import { STRUCTURE_TYPES, PRODUCTION_STEPS } from "../../../constants";
import {
  contactDisplayName,
  getCurrentProductionStep
} from "../../../utils/helpers";
import { getPreferences, setPreferences } from "../../../utils/localStorage";

import { JobName, FranchiseInfo } from "../../JobsList";
import ServiceProductionStatus from "../../ServiceProductionStatus";
import PostedTurnaround from "../../PostedTurnaround";
import CenteredLoader from "../../CenteredLoader";
import InputContainer from "../../forms/InputContainer";
import FranchiseSelect from "../../forms/FranchiseSelect";
import Select from "../../forms/Select";
import { Table, TableCell, TableRow } from "../../Table";
import EmptyMessage from "../../EmptyMessage";

const STATUS_ORDER = ["in_production", "on_hold"];

const PRODUCTION_STATUS_OPTIONS = [
  ...Object.values(PRODUCTION_STEPS["New Sale"]),
  { name: "Complete", value: "complete", shortCode: "C" }
];

const STRUCTURE_TYPE_OPTIONS = STRUCTURE_TYPES.map(type => ({
  value: type,
  label: type
}));

const renderService = service => {
  const job = service.job;
  const { contact, franchise } = job;
  const contactName = contactDisplayName(contact);
  const link = `/admin/jobs/${job.id}/services/${service.id}`;

  return (
    <TableRow key={service.id} onHold={service.status === "on_hold"}>
      <TableCell>
        <span>
          <JobName to={link}>{contactName}</JobName>
          <br />
          <FranchiseInfo franchise={franchise} />
        </span>
      </TableCell>
      <TableCell>
        {service.postedTurnaroundDate && (
          <PostedTurnaround style={{ fontSize: 16 }}>
            {service.postedTurnaroundDate}
          </PostedTurnaround>
        )}
      </TableCell>
      <TableCell>{service.structureType}</TableCell>
      <TableCell>
        <ServiceProductionStatus service={service} />
      </TableCell>
    </TableRow>
  );
};

const renderServices = ({ loading, services }) => {
  if (loading && !services) {
    return <CenteredLoader />;
  }

  if (!services || !services.length) {
    return (
      <EmptyMessage>It looks like the production queue is empty!</EmptyMessage>
    );
  }

  const sortedServices = services.slice().sort((a, b) => {
    const byStatus =
      STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status);

    if (byStatus === 0) {
      if (!a.postedTurnaroundDate) return 1;
      if (!b.postedTurnaroundDate) return -1;
      return (
        new Date(a.postedTurnaroundDate) - new Date(b.postedTurnaroundDate)
      );
    }

    return byStatus;
  });

  return (
    <Table>
      <TableRow header>
        <TableCell />
        <TableCell>Estimated Turnaround</TableCell>
        <TableCell>Structure</TableCell>
        <TableCell>Status</TableCell>
      </TableRow>
      {sortedServices.map(renderService)}
    </Table>
  );
};

const ProductionQueue = props => {
  let services = props.data.services;

  // TODO: Filter by production status
  if (services && props.productionStatus) {
    services = services
      .map(service => ({
        ...service,
        currentProductionStep: getCurrentProductionStep(service)
      }))
      .filter(({ currentProductionStep }) => {
        if (props.productionStatus === "complete") {
          return !currentProductionStep;
        }
        return (
          currentProductionStep &&
          currentProductionStep.info.value === props.productionStatus
        );
      });
  }

  return (
    <Fragment>
      <Flex pb={2}>
        <Box w={1 / 4} pr={2}>
          <InputContainer
            name="franchise"
            labelText="Franchise"
            placeholder="Filter by franchise"
            value={props.franchiseId}
            onChange={props.setFranchiseId}
            fieldComponent={FranchiseSelect}
          />
        </Box>
        <Box w={1 / 4} pl={2} pr={2}>
          <InputContainer
            name="productionStatus"
            labelText="Production Status"
            placeholder="Filter by production status"
            value={props.productionStatus}
            onChange={props.setProductionStatus}
            fieldComponent={Select}
            options={PRODUCTION_STATUS_OPTIONS}
            labelKey="name"
          />
        </Box>
        <Box w={1 / 4} pl={2} pr={2}>
          <InputContainer
            name="structureType"
            labelText="Structure Type"
            placeholder="Filter by structure type"
            value={props.structureType}
            onChange={props.setStructureType}
            fieldComponent={Select}
            options={STRUCTURE_TYPE_OPTIONS}
          />
        </Box>
      </Flex>
      {renderServices({ loading: props.data.loading, services })}
    </Fragment>
  );
};

const ProductionQueueQuery = gql`
  query ProductionQueueQuery($franchiseId: ID, $structureType: String) {
    services(
      queue: "production"
      franchiseId: $franchiseId
      structureType: $structureType
    ) {
      id
      serviceType
      submittedAt
      status
      structureType
      postedTurnaroundDate
      job {
        id
        contractNumber
        contact {
          id
          firstName
          lastName
          otherFirstName
          otherLastName
        }
        franchise {
          id
          name
          assignedInHouseTty
        }
        draftsman {
          id
          name
        }
      }
      productionSteps {
        id
        stepType
        startedAt
        completedAt
        user {
          id
          name
        }
      }
      options {
        serviceLevel
      }
    }
  }
`;

const preferenceKey = "productionQueueFilters";

const setAndSaveState = key => value => {
  const updatedState = { [key]: value };
  setPreferences(preferenceKey, updatedState);
  return updatedState;
};

const enhance = compose(
  withStateHandlers(
    () => ({
      productionStatus: "",
      structureType: "",
      franchiseId: "",
      ...getPreferences(preferenceKey)
    }),
    {
      setProductionStatus: () => setAndSaveState("productionStatus"),
      setStructureType: () => setAndSaveState("structureType"),
      setFranchiseId: () => setAndSaveState("franchiseId")
    }
  ),
  graphql(ProductionQueueQuery, {
    options: ({ franchiseId, structureType }) => ({
      variables: {
        franchiseId: franchiseId || "",
        structureType: structureType || ""
      },
      fetchPolicy: "network-only",
      pollInterval: 1000 * 30
    })
  })
);

export default enhance(ProductionQueue);



// WEBPACK FOOTER //
// ./src/components/admin/Dashboard/ProductionQueue.js